/**
 * Automatische Blockchain-Anreicherung für importierte Transaktionen.
 *
 * Läuft nach dem CSV/AI-Parse, vor dem Speichern in Firestore.
 * Nur Transfers (einzahlung/auszahlung) mit einem Hash werden angereichert —
 * Exchange-Trades sind interne Operationen ohne On-Chain-TX.
 *
 * Was wird verbessert:
 *   - zeit: präziser Blockchain-Timestamp statt CSV-Zeitstempel
 *   - menge: verifizierter On-Chain-Betrag (erkennt Fee-Abzüge)
 *   - blockchainVerified: true (für späteres Audit-Logging)
 */

const API_BASE = process.env.REACT_APP_API_BASE || '';
const ENRICH_TYPES = new Set(['einzahlung', 'auszahlung', 'staking']);
const BATCH_SIZE   = 5;   // parallele Requests
const TIMEOUT_MS   = 7000;

/** Extrahiert HH:mm:ss aus einem ISO-Timestamp-String */
function extractZeit(isoTimestamp) {
  if (!isoTimestamp) return null;
  try {
    return new Date(isoTimestamp).toISOString().slice(11, 19);
  } catch {
    return null;
  }
}

/** Extrahiert YYYY-MM-DD aus einem ISO-Timestamp-String */
function extractDatum(isoTimestamp) {
  if (!isoTimestamp) return null;
  try {
    return new Date(isoTimestamp).toISOString().slice(0, 10);
  } catch {
    return null;
  }
}

async function lookupOne(coin, hash) {
  if (!coin || !hash) return null;
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    const { authHeader } = await import('../services/authHeader');
    const auth = await authHeader();
    const res = await fetch(
      `${API_BASE}/api/blockchain-lookup?coin=${encodeURIComponent(coin)}&hash=${encodeURIComponent(hash)}`,
      { signal: controller.signal, headers: { ...auth } }
    );
    clearTimeout(timer);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null; // Timeout oder Netzwerkfehler → Import läuft ohne Anreicherung weiter
  }
}

/**
 * Reichert ein Array von gemappten Transaktionen mit Blockchain-Daten an.
 * Gibt immer das vollständige Array zurück — schlägt die Anreicherung fehl,
 * bleiben die Original-Daten erhalten.
 *
 * @param {Array} txs  Gemappte TX-Objekte mit { typ, coin, hash, datum, zeit, ... }
 * @returns {Promise<Array>} Angereichertes TX-Array
 */
export async function enrichTransactions(txs) {
  // Nur Transfers mit Hash können auf der Chain nachgeschlagen werden
  const candidates = txs.filter(tx =>
    tx.hash && ENRICH_TYPES.has(tx.typ)
  );

  if (candidates.length === 0) return txs;

  // Lookup-Ergebnisse sammeln: hash → { timestamp, amount, fee, ... }
  const resultMap = new Map();

  // Gesamt-Deadline: Anreicherung ist BEST-EFFORT und darf den Import nie blockieren.
  // Bei vielen Kandidaten, deren Hash der Lookup nicht kennt (z.B. Solana-Signaturen
  // auf der falschen Route), läuft jede Anfrage in den 7s-Timeout → der Import würde
  // minutenlang „Blockchain wird geprüft" anzeigen und das Fenster nie schließen.
  // Nach dem Budget brechen wir ab: bereits angereicherte TX behalten ihre Daten,
  // der Rest bleibt unverändert, der Import läuft sofort weiter.
  const ENRICH_DEADLINE_MS = 20000;
  const startedAt = Date.now();

  for (let i = 0; i < candidates.length; i += BATCH_SIZE) {
    if (Date.now() - startedAt > ENRICH_DEADLINE_MS) break;
    const batch = candidates.slice(i, i + BATCH_SIZE);
    const results = await Promise.allSettled(
      batch.map(tx => lookupOne(tx.coin || tx.sym || tx.coinOut || tx.symOut, tx.hash))
    );
    results.forEach((r, idx) => {
      if (r.status === 'fulfilled' && r.value?.timestamp) {
        resultMap.set(batch[idx].hash, r.value);
      }
    });
  }

  if (resultMap.size === 0) return txs;

  // Originaldaten mit Blockchain-Daten zusammenführen
  return txs.map(tx => {
    const chain = tx.hash ? resultMap.get(tx.hash) : null;
    if (!chain) return tx;

    const enriched = { ...tx, blockchainVerified: true };

    // On-Chain-Gegenadressen (lowercase) für die adressbasierte own-wallet-
    // Erkennung — nur übernehmen, wenn der Lookup sie liefert (CSV hat sie nicht).
    if (chain.from && !enriched.chainFrom) enriched.chainFrom = String(chain.from).toLowerCase();
    if (chain.to   && !enriched.chainTo)   enriched.chainTo   = String(chain.to).toLowerCase();

    // Timestamp: Blockchain-Zeit ist präziser als CSV-Export
    const chainZeit  = extractZeit(chain.timestamp);
    const chainDatum = extractDatum(chain.timestamp);
    if (chainZeit)  enriched.zeit  = chainZeit;
    if (chainDatum) enriched.datum = chainDatum;

    // Betrag: Blockchain-Betrag wenn vorhanden und plausibel (max 5% Abweichung vom CSV)
    if (chain.amount != null && tx.menge != null && tx.menge > 0) {
      const diff = Math.abs(chain.amount - tx.menge) / tx.menge;
      if (diff < 0.05) enriched.menge = chain.amount;
    }

    // Gebühr: Eine Netzwerk-Fee ist in der NATIVEN Coin der Chain (ETH-Gas, SOL-Fee).
    // → Nur in `gebuehr` (Steuerwert) schreiben, wenn die Fee-Coin der TX-Coin entspricht
    //   (= native Transfers) UND die CSV keine Gebühr lieferte (CSV-Fee hat Vorrang).
    // → Bei Token-Transfers (Gas in anderer Coin) NICHT ins gebuehr-Feld jammern, sondern
    //   separat als networkFee/networkFeeCoin mitführen (informativ, ohne Verfälschung).
    if (chain.fee != null && chain.fee > 0) {
      const feeCoin = String(chain.feeCoin || '').toUpperCase();
      const txCoin  = String(tx.coin || tx.sym || '').toUpperCase();
      const sameCoin = feeCoin && txCoin && feeCoin === txCoin;
      if (sameCoin || !feeCoin) {
        // feeCoin fehlt (Alt-Quelle) → wie bisher; feeCoin == txCoin → native Transfer.
        if (!(tx.gebuehr > 0)) enriched.gebuehr = chain.fee;
      } else {
        enriched.networkFee = chain.fee;
        enriched.networkFeeCoin = feeCoin;
      }
    }

    return enriched;
  });
}
